import 'client-portal/integration';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import { reads } from '@ember/object/computed';
import { task, timeout } from 'ember-concurrency';
import ENV from 'client-portal/config/environment';
import Service, { service } from '@ember/service';
import classic from 'ember-classic-decorator';

const REVEAL_ANIMATION_TIMEOUT_MS = 300;

@classic
export default class WebsiteWidgetService extends Service {
  @service currentPractice;
  @service session;
  @service localeManager;

  revealAnimationTimeout = REVEAL_ANIMATION_TIMEOUT_MS;

  @reads('currentPractice.schedulingWidgetApplicationId') applicationId;
  @reads('currentPractice.schedulingWidgetScopeId') scopeId;
  @reads('currentPractice.schedulingWidgetUrl') widgetsUrl;

  init() {
    super.init(...arguments);
    this.widgetInstances = {};
  }

  // NOTE: for website editor preview mode, we manually pass theme attributes in previewModel
  initWidget(previewModel, type = 'scheduling') {
    let { applicationId, widgetsUrl, scopeId } = this;

    if (!(applicationId && widgetsUrl && scopeId)) {
      return;
    }

    let nodeId = `${type}-widget-${scopeId}`;
    let node = document.getElementById(nodeId);

    // NOTE: to update preview theme we remove previous widget & reinitialize
    if (previewModel) {
      node?.remove();
    }

    let widget = this._retrieveWidget(scopeId, type);

    if (node && widget) {
      return widget;
    }

    // NOTE: widget exists but was removed from the DOM - re-append it
    if (widget) {
      this._appendWidget(widget);
      return widget;
    }

    return this._buildWidget({
      applicationId,
      nodeId,
      widgetsUrl,
      scopeId,
      previewModel,
      type,
    });
  }

  @(task(function* () {
    yield this.revealTask.perform(null, 'contact');
    yield timeout(400); // widget fade in animation delay
  }).drop())
  openContactWidgetTask;

  @(task(function* (previewModel, type = 'scheduling') {
    let widget = yield this.initWidget(previewModel, type);
    // NOTE: timeout allows for modal animation
    yield timeout(this.revealAnimationTimeout);
    widget?.reveal();
  }).drop())
  revealTask;

  _appendWidget(widget) {
    document.body.appendChild(widget.node);
  }

  _buildWidget({ applicationId, nodeId, widgetsUrl, scopeId, previewModel, type }) {
    return new Promise(resolve => {
      let node = this._buildNode(nodeId);
      let widget = new window.SPWidget(node);

      let { scopeUri, baseUrl } = this._getUrlParts(widgetsUrl);
      this._setBaseUrl(baseUrl);

      if (!previewModel) {
        this._storeWidget(widget, scopeId, type);
      }

      let datagrailConsentPreferences =
        document.cookie
          .split('; ')
          .find(row => row.startsWith('datagrail_consent_preferences='))
          ?.slice('datagrail_consent_preferences='.length) ?? null;

      widget.init({
        scopeId,
        scopeUri,
        scopeGlobal: true,
        applicationId,
        channel: this.session.channel,
        locale: this.localeManager.currentLocale,
        appearance: {
          fullScreen: true,
          colorSetId: previewModel?.colorSetId,
          theme: previewModel?.theme,
        },
        ...(type === 'contact' ? { contact: true } : {}),
        datagrailConsentPreferences,
      });

      if (isEmberTesting()) {
        return resolve(widget);
      }

      widget.iframe.addEventListener('load', () => {
        resolve(widget);
      });

      this._appendWidget(widget);
    });
  }

  _getUrlParts(widgetsUrl) {
    let { host, origin } = new URL(widgetsUrl);
    let scopeUri = host.split('.')[0];
    let baseUrl = origin.replace(`${scopeUri}.`, '');
    return { scopeUri, baseUrl };
  }

  _setBaseUrl(url) {
    if (
      ['development', 'test'].some(env => ENV.railsEnv === env || ENV.environment === env) &&
      location.port
    ) {
      url += `:${location.port}`;
    }
    window.SPWidgetBase = url;
  }

  _buildNode(nodeId) {
    let node = document.createElement('div');
    node.id = nodeId;
    return node;
  }

  _storeWidget(widget, scopeId, type) {
    this.widgetInstances[`${scopeId}-${type}`] = widget;
  }

  _retrieveWidget(scopeId, type) {
    return this.widgetInstances[`${scopeId}-${type}`];
  }
}
